import React, { forwardRef, useCallback, useEffect, useRef } from "react";

/**
 * Checkbox (DESIGN.md "Components"): a native checkbox painted by
 * .sg-check, so the platform keeps semantics, keyboard and form
 * submission. With `label` the whole row is the click target.
 *
 *   <Checkbox label="Weekdays only" checked={on} onChange={(e) => setOn(e.target.checked)} />
 *   <Checkbox aria-label="Select all" checked={all} indeterminate={some} onChange={toggleAll} />
 */
export const Checkbox = forwardRef(
  (
    { label, description, indeterminate = false, className = "", ...props },
    ref,
  ) => {
    const innerRef = useRef(null);

    const setRefs = useCallback(
      (node) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    // indeterminate is a DOM property, not an attribute.
    useEffect(() => {
      if (innerRef.current)
        innerRef.current.indeterminate = Boolean(indeterminate);
    }, [indeterminate]);

    const input = (
      <input
        ref={setRefs}
        type="checkbox"
        className={`sg-check ${label ? "" : className}`}
        aria-checked={indeterminate ? "mixed" : undefined}
        {...props}
      />
    );

    if (!label) return input;

    return (
      <label className={`sg-choice ${className}`}>
        {input}
        <span className="min-w-0">
          <span className="block">{label}</span>
          {description && (
            <span className="block text-[12px] leading-4 text-sg-ink-3">
              {description}
            </span>
          )}
        </span>
      </label>
    );
  },
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
